import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Ship, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: "Home", href: "/" },
    { name: "Services", href: "/services" },
    { name: "About", href: "/about" },
    { name: "Contact", href: "/contact" }
  ];

  const isActive = (href) => {
    if (href === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(href);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass-header" data-testid="header">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo">
            <div className="w-10 h-10 bg-[#0B2545] flex items-center justify-center">
              <Ship className="w-6 h-6 text-[#F59E0B]" />
            </div>
            <div>
              <span className="heading-font text-xl font-bold text-[#0B2545] tracking-tight">SKY CARGO</span>
              <span className="heading-font text-xl font-bold text-[#F59E0B] tracking-tight"> DELIVERY</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className={`nav-link font-medium transition-colors ${
                  isActive(link.href) 
                    ? "text-[#F59E0B]" 
                    : "text-[#0B2545] hover:text-[#F59E0B]"
                }`}
                data-testid={`nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </Link>
            ))}
            <Link to="/contact">
              <Button 
                className="btn-primary h-10 px-6 rounded-sm"
                data-testid="nav-get-quote"
              >
                Get Quote
              </Button>
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6 text-[#0B2545]" />
            ) : (
              <Menu className="w-6 h-6 text-[#0B2545]" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="mobile-menu md:hidden absolute top-20 left-0 right-0 bg-white border-t border-slate-200 shadow-lg"
              data-testid="mobile-menu"
            >
              <nav className="flex flex-col p-6">
                {navLinks.map((link) => (
                  <Link
                    key={link.name}
                    to={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`py-3 font-medium border-b border-slate-100 ${
                      isActive(link.href) 
                        ? "text-[#F59E0B]" 
                        : "text-[#0B2545] hover:text-[#F59E0B]"
                    }`}
                    data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </Link>
                ))}
                <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
                  <Button 
                    className="btn-primary h-12 w-full rounded-sm mt-4"
                    data-testid="mobile-nav-get-quote"
                  >
                    Get Quote
                  </Button>
                </Link>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default Header;
